import { decideScheduleRevisionAck } from "@northstar/dispatch-core";

export const COMPANION_REFRESH_KEY = "ns.companion.refresh";

export type CompanionRefreshMode = "constant" | "on-screen";
export type CompanionConstantMs = 15000 | 30000;

export type CompanionRefreshSettings = {
  mode: CompanionRefreshMode;
  constantMs: CompanionConstantMs;
};

export const DEFAULT_REFRESH: CompanionRefreshSettings = {
  mode: "on-screen",
  constantMs: 30000,
};

export const LIVE_POLL_MS = 1000;
/** On-screen snapshot cadence. Not stretched for server kindness. */
export const ON_SCREEN_FALLBACK_MS = 5000;

export function hydrateRefreshSettings(raw: unknown): CompanionRefreshSettings {
  const rec = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    mode: rec.mode === "constant" ? "constant" : "on-screen",
    constantMs: rec.constantMs === 15000 ? 15000 : 30000,
  };
}

export function loadCompanionRefresh(storage?: Pick<Storage, "getItem"> | null): CompanionRefreshSettings {
  try {
    const raw = storage?.getItem(COMPANION_REFRESH_KEY);
    if (!raw) return { ...DEFAULT_REFRESH };
    return hydrateRefreshSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_REFRESH };
  }
}

export function saveCompanionRefresh(
  next: CompanionRefreshSettings,
  storage?: Pick<Storage, "setItem"> | null,
): CompanionRefreshSettings {
  const hydrated = hydrateRefreshSettings(next);
  storage?.setItem(COMPANION_REFRESH_KEY, JSON.stringify(hydrated));
  return hydrated;
}

/** Whether revision/live polling should run right now. */
export function refreshPollingActive(mode: CompanionRefreshMode, visible: boolean): boolean {
  return mode === "constant" || visible;
}

export function fallbackFullPullMs(settings: CompanionRefreshSettings): number {
  return settings.mode === "constant" ? settings.constantMs : ON_SCREEN_FALLBACK_MS;
}

/** Failed board pull retries on this cadence — not on the 1s live tick. */
export const SCHEDULE_PULL_RETRY_MS = 5000;

export type CompanionChannelActions = {
  pullSchedule: boolean;
  pullLive: boolean;
};

/**
 * Board full-pull only when `/v1/revision` moved. Live kinematics pull every
 * tick once a snapshot exists — including when the revision ping is down —
 * so Mobile does not sit on a stale board lastKnown after a desk SimLink publish.
 */
export function companionChannelActions(input: {
  revisionOk: boolean;
  remoteScheduleRevision?: string;
  lastScheduleRevision: string;
  hasSnapshot: boolean;
}): CompanionChannelActions {
  const pullSchedule =
    input.revisionOk && input.remoteScheduleRevision !== input.lastScheduleRevision;
  return {
    pullSchedule,
    pullLive: input.hasSnapshot || pullSchedule,
  };
}

/**
 * Whether this tick should GET /v1/state.
 * A new schedule revision pulls immediately. A failed pull does not count as
 * applied, and it is not retried on the live (1s) cadence.
 */
export function schedulePullDue(input: {
  revisionOk: boolean;
  remoteScheduleRevision?: string;
  appliedScheduleRevision: string;
  attemptedScheduleRevision: string;
  nowMs: number;
  retryNotBeforeMs: number;
}): boolean {
  if (!input.revisionOk || !input.remoteScheduleRevision) return false;
  if (input.remoteScheduleRevision === input.appliedScheduleRevision) return false;
  if (input.remoteScheduleRevision !== input.attemptedScheduleRevision) return true;
  return input.nowMs >= input.retryNotBeforeMs;
}

/**
 * Apply a schedule ack only after /v1/state succeeded.
 * A failed pull keeps the previous ack. A hash that is not the ping is not acked.
 */
export function commitScheduleRevisionAck(input: {
  previousAck: string;
  pullOk: boolean;
  advertisedRevision?: string;
  receivedRevision?: string;
}): { ack: string; mismatch: boolean; notice: string } {
  const decision = decideScheduleRevisionAck({
    pullOk: input.pullOk,
    advertisedRevision: input.advertisedRevision,
    receivedRevision: input.receivedRevision,
  });
  if (decision.ack === null) {
    return { ack: input.previousAck, mismatch: false, notice: "" };
  }
  return { ack: decision.ack, mismatch: decision.mismatch, notice: decision.notice };
}
